import { format } from 'date-fns'

// Short human label for a pulse-run bucket date key (`yyyy-MM-dd` for day/week,
// `yyyy-MM` for month). Falls back to the API-provided label on a bad key.
export const formatBucketDate = (bucket) => {
    const key = bucket?.bucket
    if (!key) return bucket?.label || ''
    const isMonth = key.length === 7
    const date = new Date(isMonth ? `${key}-01` : key)
    if (Number.isNaN(date.getTime())) return bucket?.label || ''
    return format(date, isMonth ? 'MMM yyyy' : 'dd MMM')
}

// "Run 1 · 30 Apr" — shared by the trend chart and heatmap columns so a pulse run
// reads identically in both. `runWord` is the (translated) word for "Run".
export const runColumnLabel = (bucket, index, runWord = 'Run') => `${runWord} ${index + 1} · ${formatBucketDate(bucket)}`

export const PulseCheckList = [
    {
        id: 1,
        title: 'Emotional',
        description: 'Students feeling good',
        progress: 80,
        score: 24,
        color: '#e7f6ec',
        iconColor: '#3FA862',
        borderColor: '#d6f1df',
    },
    {
        id: 2,
        title: 'Support',
        description: 'Students felt supported',
        progress: 60,
        score: -18,
        color: '#e8f1fb',
        iconColor: '#3a7dbe',
        borderColor: '#d5e6f6',
    },
    {
        id: 3,
        title: 'Coping',
        description: 'Students coping well',
        progress: 40,
        score: 12,
        color: '#ede9fa',
        iconColor: '#6e5bc7',
        borderColor: '#ddd7f4',
    },
    {
        id: 4,
        title: 'Sleep',
        description: 'Students sleeping well',
        progress: 90,
        score: -27,
        color: '#fff5dc',
        iconColor: '#FFC545',
        borderColor: '#ffe9b0',
    },
]
export const WellbeingGlyph = ({ title, color, size = 42 }) => {
    const themeMap = {
        Emotional: 'emotional',
        Support: 'support',
        Coping: 'coping',
        Sleep: 'sleep',
    }

    const theme = themeMap[title]

    if (theme === 'emotional') {
        return (
            <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="24" cy="24" r="16" />
                <circle cx="18" cy="21" r="1.4" fill={color} stroke="none" />
                <circle cx="30" cy="21" r="1.4" fill={color} stroke="none" />
                <path d="M17 28c2 3 5 4.5 7 4.5s5-1.5 7-4.5" />
            </svg>
        )
    }

    if (theme === 'support') {
        return (
            <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M24 28s-6-3.6-6-8.4a3.6 3.6 0 0 1 6-2.7 3.6 3.6 0 0 1 6 2.7c0 4.8-6 8.4-6 8.4z" />
                <path d="M10 24v6c0 4 4 8 8 8h12c4 0 8-4 8-8v-6" />
                <path d="M10 24l4-3M38 24l-4-3" />
            </svg>
        )
    }

    if (theme === 'coping') {
        return (
            <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 38l10-18 6 10 4-6 16 14z" />
                <path d="M16 20l3 5" />
                <circle cx="33" cy="16" r="2.6" />
            </svg>
        )
    }

    if (theme === 'sleep') {
        return (
            <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M34 30A12 12 0 0 1 18 14a12 12 0 1 0 16 16z" />
                <path d="M34 12h6M37 9v6" />
            </svg>
        )
    }

    return null
}

export const PULSE_THEMES = [
    { key: 'emotional', label: 'Emotional', desc: 'Students feeling good', pct: 78, delta: +8, color: '#3FA862', bg: '#E7F6EC', ringBg: '#D6F1DF' },
    { key: 'support', label: 'Support', desc: 'Students felt supported', pct: 74, delta: +5, color: '#3A7DBE', bg: '#E8F1FB', ringBg: '#D5E6F6' },
    { key: 'coping', label: 'Coping', desc: 'Students coping well', pct: 69, delta: -4, color: '#6E5BC7', bg: '#EDE9FA', ringBg: '#DDD7F4' },
    { key: 'sleep', label: 'Sleep', desc: 'Students sleeping well', pct: 72, delta: +3, color: '#FFC545', bg: '#FFF5DC', ringBg: '#FFE9B0' },
]

export const HEATMAP = [
    { name: 'Amelia R.', row: [4, 4, 4, 5, 5, 4, 3, 5] },
    { name: 'Ben K.', row: [3, 3, 4, 4, 4, 4, 4, 4] },
    { name: 'Chloe S.', row: [4, 3, 3, 4, 4, 4, 4, 4] },
    { name: 'Daniel M.', row: [3, 3, 2, 2, 3, 3, 3, 3] },
    { name: 'Ella W.', row: [4, 4, 2, 3, 3, 3, 2, 3] },
    { name: 'Finn J.', row: [3, 3, 2, 3, 3, 2, 2, 3] },
    { name: 'Grace L.', row: [5, 5, 4, 5, 5, 4, 4, 4] },
    { name: 'Harper T.', row: [4, 4, 4, 4, 4, 4, 3, 4] },
    { name: 'Isaac B.', row: [2, 3, 3, 3, 3, 3, 3, 3] },
    { name: 'Jade P.', row: [5, 5, 5, 5, 5, 2, 2, 2] },
]
export const HEATMAP_YEARS = [
    { name: 'Year 7', row: [4, 4, 4, 1, 5, 4, 4, 4] },
    { name: 'Year 8', row: [3, 3, 4, 4, 4, 4, 4, 4] },
    { name: 'Year 9', row: [3, 3, 3, 3, 3, 3, 3, 3] },
    { name: 'Year 10', row: [4, 4, 3, 3, 4, 4, 3, 4] },
    { name: 'Year 11', row: [3, 3, 2, 2, 3, 3, 3, 3] },
    { name: 'Year 12', row: [2, 1, 2, 3, 3, 3, 3, 3] },
]
export const HEATMAP_THEMES = [
    { key: 'overall', label: 'Overall', color: '#5E639B', offset: 0.0 },
    { key: 'emotional', label: 'Emotional', color: '#FC8E66', offset: 0.3 },
    { key: 'support', label: 'Support', color: '#5BB585', offset: -0.2 },
    { key: 'coping', label: 'Coping', color: '#7E7DD1', offset: -0.8 },
    { key: 'sleep', label: 'Sleep', color: '#5DBEEE', offset: 0.2 },
]
export const RESPONSE_SCALE = [
    { v: 5, label: 'Very High', color: '#2B8C50' },
    { v: 4, label: 'High', color: '#6FCC87' },
    { v: 3, label: 'Moderate', color: '#FDDF6D' },
    { v: 2, label: 'Low', color: '#FF9F6B' },
    { v: 1, label: 'Very Low', color: '#FC4C59' },
]
export const TREND = {
    weeks: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7', 'Week 8'],
    dates: ['06. Mar - 13. Mar', '13. Mar - 20. Mar', '20 Mar', '27 Mar', '03 Apr', '10 Apr', '17 Apr', '24 Apr'],
    series: {
        emotional: [3.8, 3.9, 1.6, 3.3, 3.9, 4.0, 1.1, 4.2],
        support: [3.5, 3.6, 3.5, 3.2, 3.7, 3.7, 3.8, 3.8],
        coping: [3.4, 3.3, 3.1, 4.9, 3.2, 3.3, 3.1, 3.1],
        sleep: [3.0, 2.9, 2.7, 2.5, 2.9, 5.0, 2.8, 2.9],
    },
}
export const HEAT_COLOR = {
    5: { bg: '#2B8C50', fg: '#fff' },
    4: { bg: '#6FCC87', fg: '#16382A' },
    3: { bg: '#FDDF6D', fg: '#5A4A14' },
    2: { bg: '#FF9F6B', fg: '#5C2A14' },
    1: { bg: '#FC4C59', fg: '#fff' },
}

// Discrete heat color for a 1–5 value. Decimal averages are rounded to the
// nearest band so cells use the fixed palette rather than a smooth gradient.
export const heatColor = (value) => HEAT_COLOR[Math.max(1, Math.min(5, Math.round(value)))]

// ------------------------------------------------------------------ //
// Survey analytics mapping helpers
//
// The POST /api/survey_analytics response uses dynamic `group` names. These
// helpers map that aggregated payload (summary/groups/trend) onto the shapes
// the existing Pulse Check cards expect, reusing the PULSE_THEMES palette where
// a group name matches a known theme and falling back to a rotating palette for
// unknown groups.
// ------------------------------------------------------------------ //

const GROUP_FALLBACK_PALETTE = [
    { color: '#5E639B', bg: '#ECEBF8', ringBg: '#DDDCF1' },
    { color: '#3A7DBE', bg: '#E8F1FB', ringBg: '#D5E6F6' },
    { color: '#3FA862', bg: '#E7F6EC', ringBg: '#D6F1DF' },
    { color: '#6E5BC7', bg: '#EDE9FA', ringBg: '#DDD7F4' },
    { color: '#FFC545', bg: '#FFF5DC', ringBg: '#FFE9B0' },
    { color: '#E0698A', bg: '#FCE9EF', ringBg: '#F7D4E0' },
]

// Resolve the palette for a group, matched by name against PULSE_THEMES with a
// deterministic fallback by index so colors stay stable for a given ordering.
export const getGroupTheme = (group, index = 0) => {
    const key = String(group || '')
        .toLowerCase()
        .trim()
    const match = PULSE_THEMES.find((theme) => theme.key === key || theme.label.toLowerCase() === key)
    if (match) {
        return { color: match.color, bg: match.bg, ringBg: match.ringBg, desc: match.desc }
    }
    const fallback = GROUP_FALLBACK_PALETTE[index % GROUP_FALLBACK_PALETTE.length]
    return { ...fallback, desc: '' }
}

// Map a 1-5 average onto 0-100 (mirrors the backend's percentage mapping).
export const averageToPercentage = (average) => {
    if (average === null || average === undefined) return 0
    const pct = ((average - 1) / 4) * 100
    return Math.round(Math.max(0, Math.min(100, pct)))
}

// "vs last week" delta (percentage points) for a group, derived from trend.
// Returns null when fewer than two buckets contain the group (nothing to compare).
export const getGroupDelta = (group, trend = []) => {
    const values = (trend || []).filter((bucket) => bucket?.groups && bucket.groups[group] !== null && bucket.groups[group] !== undefined).map((bucket) => bucket.groups[group])
    if (values.length < 2) return null
    const last = values[values.length - 1]
    const prev = values[values.length - 2]
    return averageToPercentage(last) - averageToPercentage(prev)
}

// Ordered group names from an analytics payload.
export const groupKeys = (analytics) => (analytics?.groups || []).map((g) => g.group)

// Label for the delta comparison, matching the trend granularity returned by the
// API ('day' | 'week' | 'month'). Falls back to weekly wording.
export const comparisonLabel = (granularity) => {
    switch (granularity) {
        case 'run':
            return 'vs previous run'
        case 'day':
            return 'vs previous day'
        case 'month':
            return 'vs last month'
        case 'week':
        default:
            return 'vs last week'
    }
}

// Shape the `groups` payload for PulseCategoryList `items`.
export const mapGroupsToCategories = (groups = [], trend = []) =>
    (groups || []).map((g, index) => {
        const theme = getGroupTheme(g.group, index)
        return {
            id: g.group,
            title: g.group,
            description: theme.desc || '',
            progress: Math.round(g.percentage ?? averageToPercentage(g.average)),
            score: getGroupDelta(g.group, trend),
            color: theme.bg,
            iconColor: theme.color,
            borderColor: theme.ringBg,
        }
    })

// ------------------------------------------------------------------ //
// Per-student pulse stats (Needs Attention / Student list)
// Derived from a heatmap student row: { id, name, year, cells: [{overall}] }.
// ------------------------------------------------------------------ //
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null)

export const studentPulseStats = (row) => {
    const series = (row?.cells || []).map((c) => (c && c.overall !== null && c.overall !== undefined ? c.overall : null))
    const present = series.filter((v) => v !== null)
    const recent = avg(present.slice(-3))
    const prior = avg(present.slice(0, 3))
    const latest = present.length ? present[present.length - 1] : null
    const delta = recent !== null && prior !== null ? +(recent - prior).toFixed(1) : 0
    return {
        id: row?.id,
        name: row?.name,
        year: row?.year || '',
        series,
        latest: latest !== null ? +latest.toFixed(1) : null,
        recent: recent !== null ? +recent.toFixed(1) : null,
        delta,
        hasData: present.length > 0,
    }
}

export const PULSE_STATUS = {
    risk: { key: 'risk', label: 'At risk', bg: '#FDECEE', fg: '#C73340', dot: '#FC4C59' },
    declining: { key: 'declining', label: 'Declining', bg: '#FFF1E5', fg: '#B25D14', dot: '#F08A2A' },
    watch: { key: 'watch', label: 'Watch', bg: '#FFF5DC', fg: '#8A6A12', dot: '#E0B62C' },
    ok: { key: 'ok', label: 'Stable', bg: '#E7F6EC', fg: '#2B8C50', dot: '#3FA862' },
    none: { key: 'none', label: 'No data', bg: '#F4F3FA', fg: '#9392AD', dot: '#BEBECE' },
}

export const pulseStatus = (stats) => {
    if (!stats || !stats.hasData) return PULSE_STATUS.none
    if (stats.recent < 2.6) return PULSE_STATUS.risk
    if (stats.delta <= -0.6) return PULSE_STATUS.declining
    if (stats.recent < 3.5) return PULSE_STATUS.watch
    return PULSE_STATUS.ok
}

// True for students who warrant attention (low recent score or a clear decline).
export const needsAttention = (stats) => stats.hasData && (stats.recent < 3.3 || stats.delta <= -0.6)

// Shape the `groups` payload for VsLastWeekCard rows.
export const mapGroupsToDeltas = (groups = [], trend = []) =>
    (groups || []).map((g, index) => {
        const theme = getGroupTheme(g.group, index)
        return {
            key: g.group,
            label: g.group,
            color: theme.color,
            delta: getGroupDelta(g.group, trend),
        }
    })
